/*
  Product card behaviour — snippets/product-card.liquid
  -----------------------------------------------------
  Everything here is progressive enhancement: the card renders and is fully usable
  with this file blocked. Five jobs:

    • Media track. Native scroll-snap does the scrolling; this drives the two hover
      arrows and their disabled states, pages the track on a horizontal wheel or
      trackpad gesture, and cancels the slide's link when the pointer dragged rather
      than clicked.

    • Colour swatches. Server-rendered as links that open the product on that colour
      alone (`?option_values=<value id>`), or to the sibling product for a combined
      listing's colour, with none selected on landing. Clicking one marks it, scrolls the
      track to that colour's media in place instead of navigating, swaps the card's price
      line for the one the chip carries, and re-points the card's other links — quick add
      included — at the swatch's own href, which is what carries the colour through to
      the page and the sheet. A slide showing a colour keeps its own link to that colour
      (no `data-pc-link`): it is the picture the shopper clicked. Nothing carries a size.

    • Quick add. Dispatches a cancelable `quick-shop:open` and lets the anchor
      navigate when nothing listens — the same contract `_lifestyle-product-card`
      and `_mini-product-block` use.

    • Compare. Selection persists in sessionStorage, survives a grid re-render, and
      every change re-broadcasts the whole selection as `product-compare:change`.

    • Wishlist. Dispatches a cancelable `wishlist:toggle` from the button and flips its
      own state only when nothing listens — assets/wishlist.js owns it otherwise.

    • Landing nudge. On mobile, the first card peeks at its next slide once per page
      load so the track reads as a gallery rather than a photograph.

  Listeners are delegated from `document`, so cards appended by Load More or swapped
  in by a filter change need no re-initialisation. The two pieces of per-card state
  that cannot be delegated — arrow disabled states and restoring compare ticks — run
  on `product-grid:updated`, which assets/product-grid.js fires after both.
*/
(function () {
  if (window.__productCard) return;
  window.__productCard = true;

  var COMPARE_KEY = '7mesh:compare';
  var DRAG_TOLERANCE = 6;

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- Media track ---------- */

  function trackOf(el) {
    var card = el.closest('[data-product-card]');
    return card ? card.querySelector('[data-pc-track]') : null;
  }

  function syncArrows(track) {
    var card = track.closest('[data-product-card]');
    if (!card) return;
    var prev = card.querySelector('[data-pc-prev]');
    var next = card.querySelector('[data-pc-next]');
    if (!prev || !next) return;

    // A fractional scrollWidth on a zoomed or high-DPI viewport lands a pixel short
    // of the true end, so the last page would never disable `next`.
    var max = track.scrollWidth - track.clientWidth;
    prev.disabled = track.scrollLeft <= 1;
    next.disabled = track.scrollLeft >= max - 1;
  }

  function step(track, direction) {
    track.scrollBy({
      left: direction * track.clientWidth,
      behavior: reducedMotion() ? 'auto' : 'smooth',
    });
  }

  function goToMedia(track, mediaId) {
    var slides = track.querySelectorAll('[data-pc-slide]');
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].getAttribute('data-media-id') !== mediaId) continue;
      // Index maths, not offsetLeft: every slide is exactly one track width, and
      // offsetLeft is measured from the positioned media box rather than the track.
      track.scrollTo({
        left: i * track.clientWidth,
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
      return true;
    }
    return false;
  }

  /* ---------- Swatches ---------- */

  function selectSwatch(swatch) {
    var row = swatch.closest('[data-pc-swatches]');
    if (!row) return;

    var all = row.querySelectorAll('[data-pc-swatch]');
    for (var i = 0; i < all.length; i++) {
      all[i].classList.toggle('is-selected', all[i] === swatch);
      if (all[i] === swatch) {
        all[i].setAttribute('aria-current', 'true');
      } else {
        all[i].removeAttribute('aria-current');
      }
    }

    updateBadges(swatch);
    updatePrice(swatch);
    retargetLinks(swatch);
  }

  /* The chip carries its colour's price line as a <template>, rendered by Liquid so the
     copy and the money format stay there. A sibling colour carries none and the line
     is left as it is. */
  function updatePrice(swatch) {
    var card = swatch.closest('[data-product-card]');
    var line = card ? card.querySelector('[data-pc-price-line]') : null;
    var tpl = swatch.querySelector('template[data-pc-price]');
    if (!line || !tpl) return;

    line.textContent = '';
    line.appendChild(tpl.content.cloneNode(true));
  }

  /* The chip stays on the page, so every other way out of the card has to carry the colour
     the shopper picked — otherwise the PDP opens on its first colour. The swatch's own
     href is the one already resolved server-side, so it is copied verbatim. */
  function retargetLinks(swatch) {
    var card = swatch.closest('[data-product-card]');
    var href = swatch.getAttribute('href');
    if (!card || !href) return;

    var links = card.querySelectorAll('[data-pc-link]');
    for (var i = 0; i < links.length; i++) links[i].setAttribute('href', href);
  }

  /* The badge row holds every colour's state, so a swatch only has to name its own — the
     PDP names a variant instead and the same driver answers both. */
  function updateBadges(swatch) {
    var card = swatch.closest('[data-product-card]');
    if (!card || !card.querySelector('[data-product-badges]')) return;

    var group = swatch.getAttribute('data-badge-group');
    if (group == null) return;

    card.dispatchEvent(
      new CustomEvent('product-badges:update', {
        bubbles: true,
        detail: { group: group }
      })
    );
  }

  /* ---------- Compare ---------- */

  function readCompare() {
    try {
      var raw = window.sessionStorage.getItem(COMPARE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Object.prototype.toString.call(parsed) === '[object Array]' ? parsed : [];
    } catch (e) {
      // Private browsing, a full quota, or a value another tab corrupted. Compare
      // simply stops remembering; the checkbox itself keeps working.
      return [];
    }
  }

  function writeCompare(ids) {
    try {
      window.sessionStorage.setItem(COMPARE_KEY, JSON.stringify(ids));
    } catch (e) {
      /* see readCompare */
    }
  }

  function broadcastCompare(ids, changedId, checked) {
    document.dispatchEvent(
      new CustomEvent('product-compare:change', {
        detail: { ids: ids, productId: changedId, selected: checked },
        bubbles: true,
      })
    );
  }

  function toggleCompare(input) {
    var ids = readCompare();
    var id = input.value;
    var at = ids.indexOf(id);

    if (input.checked && at === -1) {
      ids.push(id);
    } else if (!input.checked && at !== -1) {
      ids.splice(at, 1);
    }

    writeCompare(ids);
    broadcastCompare(ids, id, input.checked);
  }

  // Re-ticks boxes after a re-render. Runs against a root so a Load More append only
  // walks the new cards.
  function restoreCompare(root) {
    var ids = readCompare();
    if (!ids.length) return;
    var inputs = (root || document).querySelectorAll('[data-pc-compare]');
    for (var i = 0; i < inputs.length; i++) {
      if (ids.indexOf(inputs[i].value) !== -1) inputs[i].checked = true;
    }
  }

  /* ---------- Wiring ---------- */

  // Scroll does not bubble, so the capture phase is the only way to hear it for
  // cards that do not exist yet.
  document.addEventListener(
    'scroll',
    function (e) {
      var el = e.target;
      if (!el || !el.hasAttribute || !el.hasAttribute('data-pc-track')) return;
      syncArrows(el);
    },
    true
  );

  /* Trackpads and horizontal wheels. The nav overlay covers the media and sits outside the
     track, so a gesture over it has no scrollable ancestor to reach and the card never
     moved; the card catches it instead and drives its own track. Snap is mandatory and the
     slides are full width, so it pages rather than following the gesture 1:1. */
  var wheelTrack = null;
  var wheelDelta = 0;
  var wheelIdle = null;

  document.addEventListener(
    'wheel',
    function (e) {
      // Vertical intent belongs to the page.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;

      var card = e.target && e.target.closest ? e.target.closest('[data-product-card]') : null;
      var track = card && card.querySelector('[data-pc-track]');
      if (!track || track.scrollWidth <= track.clientWidth + 1) return;
      // Inside a mounted carousel the gesture moves the rail (assets/carousel.js), not the card.
      if (card.closest('.splide.is-initialized')) return;

      e.preventDefault();

      // A new track starts its own gesture, or the last one's remainder pages this one.
      if (wheelTrack !== track) {
        wheelTrack = track;
        wheelDelta = 0;
      }

      // A line- or page-mode wheel reports 1s and 2s; scale them to something moving.
      wheelDelta += e.deltaX * (e.deltaMode === 0 ? 1 : 16);
      if (Math.abs(wheelDelta) >= 40) {
        step(track, wheelDelta > 0 ? 1 : -1);
        wheelDelta = 0;
      }

      // No wheel event says it is the last one, so the end of the gesture is a gap: drop
      // the remainder rather than letting it page on the next flick.
      if (wheelIdle) clearTimeout(wheelIdle);
      wheelIdle = setTimeout(function () {
        wheelIdle = null;
        wheelTrack = null;
        wheelDelta = 0;
      }, 120);
    },
    { passive: false }
  );

  var dragOrigin = null;

  document.addEventListener('pointerdown', function (e) {
    if (!e.target || !e.target.closest) return;
    var track = e.target.closest('[data-pc-track]');
    dragOrigin = track ? { track: track, left: track.scrollLeft } : null;
  });

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var arrow = e.target.closest('[data-pc-prev], [data-pc-next]');
    if (arrow) {
      var arrowTrack = trackOf(arrow);
      if (arrowTrack) step(arrowTrack, arrow.hasAttribute('data-pc-next') ? 1 : -1);
      // A card holding focus keeps its hover treatments lit after the shopper has
      // moved on. The arrows are `tabindex="-1"`, so nothing is lost by dropping it.
      arrow.blur();
      return;
    }

    var more = e.target.closest('[data-pc-swatch-more]');
    if (more) {
      var row = more.closest('[data-pc-swatches]');
      if (row) {
        var open = row.classList.toggle('is-expanded');
        more.setAttribute('aria-expanded', open ? 'true' : 'false');
        more.setAttribute(
          'aria-label',
          more.getAttribute(open ? 'data-label-less' : 'data-label-more') || ''
        );
      }
      return;
    }

    var swatch = e.target.closest('[data-pc-swatch]');
    if (swatch) {
      var swatchTrack = trackOf(swatch);
      var mediaId = swatch.getAttribute('data-media-id');

      /* Always stays on the page, even for a colour with no media to scroll to: one
         chip must not mean something different from its neighbours. The anchor is
         still a real link, so with JS off every chip opens the product page. */
      selectSwatch(swatch);
      e.preventDefault();
      if (swatchTrack && mediaId) goToMedia(swatchTrack, mediaId);
      return;
    }

    var quickAdd = e.target.closest('[data-pc-quick-add]');
    if (quickAdd) {
      var opened = new CustomEvent('quick-shop:open', {
        detail: {
          handle: quickAdd.getAttribute('data-pc-quick-add'),
          url: quickAdd.getAttribute('href'),
          source: 'product-card',
        },
        cancelable: true,
        bubbles: true,
      });
      if (!quickAdd.dispatchEvent(opened)) e.preventDefault();
      return;
    }

    var wish = e.target.closest('[data-pc-wishlist]');
    if (wish) {
      var next = wish.getAttribute('aria-pressed') !== 'true';
      var live = wish.dispatchEvent(
        new CustomEvent('wishlist:toggle', {
          detail: {
            handle: wish.getAttribute('data-product-handle'),
            productId: wish.getAttribute('data-product-id'),
            active: next,
            source: 'product-card',
          },
          cancelable: true,
          bubbles: true,
        })
      );
      if (live) wish.setAttribute('aria-pressed', next ? 'true' : 'false');
      return;
    }

    var slide = e.target.closest('[data-pc-slide]');
    if (slide && dragOrigin && dragOrigin.track.contains(slide)) {
      if (Math.abs(dragOrigin.track.scrollLeft - dragOrigin.left) > DRAG_TOLERANCE) {
        e.preventDefault();
      }
    }
  });

  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.hasAttribute) return;
    if (e.target.hasAttribute('data-pc-compare')) toggleCompare(e.target);
  });

  /* ---------- Landing nudge ---------- */

  /* The first card only, once per page load, never on a re-render: a hint that
     repeats every time the grid changes is an animation, not a hint. */
  function nudgeFirstCard() {
    if (reducedMotion()) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    var card = document.querySelector('[data-grid-items] [data-product-card]');
    var track = card && card.querySelector('[data-pc-track]');
    if (!track || track.querySelectorAll('[data-pc-slide]').length < 2) return;

    function play() {
      // The shopper beat us to it. Nudging now would fight a real swipe.
      if (track.scrollLeft > 0) return;
      track.classList.add('is-nudging');

      function stop() {
        track.classList.remove('is-nudging');
        track.removeEventListener('animationend', stop);
        track.removeEventListener('pointerdown', stop);
        track.removeEventListener('touchstart', stop);
      }

      // animationend bubbles from whichever slide finishes first, which is all of
      // them at once — the first one to arrive tidies up for the rest.
      track.addEventListener('animationend', stop);
      track.addEventListener('pointerdown', stop);
      track.addEventListener('touchstart', stop);
    }

    // A hint nobody sees is not a hint: a tall banner above the grid can leave the
    // first row below the fold on landing.
    if (!('IntersectionObserver' in window)) {
      setTimeout(play, 600);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          observer.disconnect();
          // Let the page settle first, so the nudge is not read as the layout still
          // shuffling images into place.
          setTimeout(play, 400);
          return;
        }
      },
      { threshold: 0.6 }
    );
    observer.observe(card);
  }

  /* ---------- Per-card state ---------- */

  function initCards(root) {
    var scope = root || document;
    restoreCompare(scope);
    var tracks = scope.querySelectorAll('[data-pc-track]');
    for (var i = 0; i < tracks.length; i++) syncArrows(tracks[i]);
  }

  function ready() {
    initCards(document);
    nudgeFirstCard();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }

  // Fired by assets/product-grid.js after a Load More append or a filter re-render.
  document.addEventListener('product-grid:updated', function (e) {
    initCards(e.detail && e.detail.root ? e.detail.root : document);
  });

  // Theme editor lifecycle.
  document.addEventListener('shopify:section:load', function (e) {
    initCards(e.target);
  });

  // Cards resize with the viewport, and a track that was at its end can stop being
  // there — the arrows would keep their stale disabled states otherwise.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var tracks = document.querySelectorAll('[data-pc-track]');
      for (var i = 0; i < tracks.length; i++) syncArrows(tracks[i]);
    }, 200);
  });
})();
